import { defineStore } from 'pinia'
import { authApi } from '@/api'
import type { AuthStoreState, JwtClaims } from '@/types'

export const useAuthStore = defineStore('auth', {
  state: (): AuthStoreState => ({
    username: '',
    apiKey: '',
    rememberMe: false,
    loggedIn: false,
    claims: {},
  }),

  persist: {
    key: 'apiAuth',
    storage: localStorage,
    pick: ['username', 'apiKey', 'rememberMe', 'loggedIn', 'claims'],
    afterHydrate: ({ store }) => {
      if (!store.rememberMe) {
        // TODO: proper auto-logout, no key written to store without rememberMe, key encrypted (?)
        store.clearAuth()
      }
    },
  },

  getters: {
    isAuthenticated: (state) => state.loggedIn && state.apiKey.length > 0,
  },

  actions: {
    /** Fetches token scopes from the API. Returns null on failure. */
    async fetchTokenScopes(apiKey: string): Promise<JwtClaims | null> {
      try {
        // Set the key temporarily so the interceptor can use it
        this.apiKey = apiKey
        
        const response = await authApi.scopes()
        return {
          scopes: response.scopes ?? [],
          isAdmin: response.isAdmin ?? false,
          clientName: response.clientName ?? '',
        }
      } catch {
        // Clear on failure to avoid partial state
        this.apiKey = ''
        return null
      }
    },

    /** Stores a successful login. Returns false when the key is not a usable token or scopes fail to fetch. */
    async setAuth(username: string, key: string, rememberMe: boolean = false) {
      // Fetch scopes from API - hard gate (pass the key for auth header)
      const claims = await this.fetchTokenScopes(key)
      if (!claims) {
        // Clear temporarily set key on failure
        this.apiKey = ''
        return false
      }

      this.username = username
      this.apiKey = key
      this.rememberMe = rememberMe
      this.claims = claims
      this.loggedIn = true

      return this.loggedIn
    },

    /** clear everything - called on logout or page reload */
    clearAuth() {
      this.username = ''
      this.apiKey = ''
      this.rememberMe = false
      this.loggedIn = false
      this.claims = {}
    },

    hasClaim(claim: string) {
      const { role } = this.claims

      if (!role) {
        return false
      }

      return Array.isArray(role) ? role.includes(claim) : role === claim
    },
  },
})
